import { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { User, Lock, Eye, EyeSlash, CheckCircle, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ProfileSettings({ open, onOpenChange }) {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('info'); // 'info' or 'password'
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleSubmitPassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Parolele noi nu coincid');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Parola nouă trebuie să aibă cel puțin 6 caractere');
      return;
    }

    setIsLoading(true);

    try {
      await axios.put(
        `${API_URL}/api/auth/change-password`,
        {
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        },
        { withCredentials: true }
      );
      
      setPasswordChanged(true);
      toast.success('Parola a fost schimbată cu succes!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      
      setTimeout(() => {
        setPasswordChanged(false);
        setActiveSection('info');
      }, 2000);
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Eroare la schimbarea parolei';
      toast.error(errorMsg);
    }

    setIsLoading(false);
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="profile-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User weight="duotone" className="w-5 h-5 text-primary" />
            Setări Profil
          </DialogTitle>
          <DialogDescription>
            Gestionează informațiile contului tău
          </DialogDescription>
        </DialogHeader>

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-border pb-2">
          <button
            onClick={() => setActiveSection('info')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeSection === 'info' 
                ? 'bg-primary/10 text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="profile-info-tab"
          >
            Informații
          </button>
          <button
            onClick={() => setActiveSection('password')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeSection === 'password' 
                ? 'bg-primary/10 text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid="profile-password-tab"
          >
            Schimbă Parola
          </button>
        </div>

        {/* Info Section */}
        {activeSection === 'info' && (
          <div className="space-y-4 py-4" data-testid="profile-info-section">
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">Nume</Label>
              <div className="p-3 bg-muted rounded-lg font-medium">{user?.name}</div>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">Email</Label>
              <div className="p-3 bg-muted rounded-lg font-medium">{user?.email}</div>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">CUI (Cod Unic Identificare)</Label>
              <div className="p-3 bg-muted rounded-lg font-medium">{user?.cui}</div>
            </div>
            <p className="text-xs text-muted-foreground">
              Pentru modificarea numelui sau email-ului, contactați suportul.
            </p>
          </div>
        )}

        {/* Password Section */}
        {activeSection === 'password' && (
          <div className="py-4" data-testid="profile-password-section">
            {passwordChanged ? (
              <div className="text-center py-8">
                <CheckCircle weight="fill" className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground">Parola schimbată!</h3>
                <p className="text-muted-foreground">Noua parolă este activă.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmitPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Parola curentă</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      name="currentPassword"
                      type={showPasswords.current ? "text" : "password"}
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      required
                      className="pr-10"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('current')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPasswords.current ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">Parola nouă</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      name="newPassword"
                      type={showPasswords.new ? "text" : "password"}
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      required
                      className="pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('new')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPasswords.new ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmă parola nouă</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPasswords.confirm ? "text" : "password"}
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      required
                      className="pr-10"
                      data-testid="confirm-new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('confirm')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPasswords.confirm ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full gap-2"
                  disabled={isLoading}
                  data-testid="change-password-submit"
                >
                  {isLoading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      Se procesează...
                    </>
                  ) : (
                    <>
                      <Lock weight="bold" className="w-4 h-4" />
                      Schimbă Parola
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
